import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import MapBackground from './MapBackground';

const ASSET_TYPES = [
  {
    id: 'telecoms_sites',
    name: 'Telecoms Sites',
    description: 'FTN, GSM-R, radio and equipment rooms',
    colour: '#ff8c00',
    icon: '📡',
  },
  {
    id: 'spt_sites',
    name: 'Signals & Points',
    description: 'Rail signals and points locations',
    colour: '#d32f2f',
    icon: '🚦',
  },
  {
    id: 'sdh_sites',
    name: 'SDH Sites',
    description: 'Synchronous Digital Hierarchy nodes',
    colour: '#1e88e5',
    icon: '🔗',
  },
  {
    id: 'pdh_sites',
    name: 'PDH Sites',
    description: 'Plesiochronous Digital Hierarchy nodes',
    colour: '#7b1fa2',
    icon: '🔌',
  },
  {
    id: 'battery_sites',
    name: 'UPS & Battery',
    description: 'Uninterruptible power supply sites',
    colour: '#e67e00',
    icon: '🔋',
  },
  {
    id: 'loc_sites',
    name: 'LOC Sites',
    description: 'Location cabinets',
    colour: '#e67e00',
    icon: '🗄️',
  },
  {
    id: 'fibre_sites',
    name: 'Fibre Cables',
    description: 'Fibre optic cable joints across the network',
    colour: '#00bcd4',
    icon: '🔵',
  },
];

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Styled components
const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  min-height: var(--app-height, 100vh);
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Card = styled.div`
  background: rgba(20, 20, 20, 0.92);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 32px 28px;
  width: 95%;
  max-width: 520px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.06);
  z-index: 10;
  animation: ${fadeIn} 0.5s ease;

  @media (max-width: 480px) {
    padding: 24px 18px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 28px;
  animation: ${fadeInUp} 0.6s ease;
`;

const Logo = styled.h1`
  color: white;
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 4px;
  letter-spacing: -0.5px;

  @media (max-width: 480px) {
    font-size: 26px;
  }
`;

const WelcomeText = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  margin: 0;
`;

const UserEmail = styled.span`
  color: #4ade80;
  font-weight: 500;
`;

const SectionLabel = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 14px;
  animation: ${fadeInUp} 0.6s ease 0.1s both;
`;

const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 24px;

  @media (min-width: 480px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const AssetCard = styled.button<{ accentColour: string; delay: number }>`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 18px 12px 14px;
  cursor: pointer;
  text-align: center;
  transition: all 0.25s ease;
  animation: ${fadeInUp} 0.5s ease ${props => 0.15 + props.delay * 0.06}s both;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.accentColour};
    opacity: 0.7;
    transition: opacity 0.25s ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: ${props => props.accentColour}40;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const CardIcon = styled.div`
  font-size: 28px;
  margin-bottom: 8px;
  line-height: 1;
`;

const CardName = styled.div`
  color: white;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const CardDescription = styled.div`
  color: rgba(255, 255, 255, 0.35);
  font-size: 11px;
  line-height: 1.3;
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 4px 0 20px;
`;

const QuickActions = styled.div`
  display: flex;
  gap: 10px;
  animation: ${fadeInUp} 0.5s ease 0.55s both;

  @media (max-width: 380px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 14px 16px;
  border-radius: 10px;
  border: ${props => props.variant === 'secondary' 
    ? '1px solid rgba(255, 255, 255, 0.1)' 
    : 'none'};
  background: ${props => props.variant === 'secondary' 
    ? 'rgba(255, 255, 255, 0.04)' 
    : 'linear-gradient(135deg, #4fa865, #3d8a50)'};
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.variant === 'secondary' 
      ? '0 4px 12px rgba(0, 0, 0, 0.2)' 
      : '0 6px 20px rgba(79, 168, 101, 0.35)'};
    background: ${props => props.variant === 'secondary' 
      ? 'rgba(255, 255, 255, 0.07)' 
      : 'linear-gradient(135deg, #5cb873, #4a9c5c)'};
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 20px;
  color: rgba(255, 255, 255, 0.2);
  font-size: 11px;
  animation: ${fadeIn} 0.5s ease 0.7s both;
`;

const SignOutLink = styled.span`
  color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: rgba(255, 255, 255, 0.6);
  }
`;

interface LandingPageProps {
  onAssetSelect: (assetType: string) => void;
  onBrowseAll: () => void;
  onSignOut: () => void;
  userEmail: string;
}

const LandingPage: React.FC<LandingPageProps> = ({
  onAssetSelect,
  onBrowseAll,
  onSignOut,
  userEmail,
}) => {
  useEffect(() => {
    const handleResize = () => {
      const appHeight = window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${appHeight}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <MapBackground interactive={true} />
      <Container>
        <Card>
          <Header>
            <Logo>JPNWR Maps</Logo>
            <WelcomeText>
              Welcome, <UserEmail>{userEmail}</UserEmail>
            </WelcomeText>
          </Header>

          <SectionLabel>Select an asset type</SectionLabel>

          <AssetGrid>
            {ASSET_TYPES.map((asset, index) => (
              <AssetCard
                key={asset.id}
                accentColour={asset.colour}
                delay={index}
                onClick={() => onAssetSelect(asset.id)}
                title={asset.description}
              >
                <CardIcon>{asset.icon}</CardIcon>
                <CardName>{asset.name}</CardName>
                <CardDescription>{asset.description}</CardDescription>
              </AssetCard>
            ))}
          </AssetGrid>

          <Divider />

          <QuickActions>
            <ActionButton onClick={onBrowseAll}>
              Back to Main Menu
            </ActionButton>
          </QuickActions>

          <Footer>
            <SignOutLink>JPNWR Maps v2.0</SignOutLink>
          </Footer>
        </Card>
      </Container>
    </>
  );
};

export default LandingPage;
